import React, {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {KTSearchBox} from 'helpers/ui-components/KTSearchBox'
import KTTable from 'helpers/ui-components/KTTable'
import KTPagination from 'helpers/ui-components/KTPagination'
import {useListController} from 'hooks/useListController'
import {
 ColumnProps,
 IFormData,
 IFormField,
} from 'models/Common'
import Spinner from 'layout/common/Spinner'
import {useNavigate} from 'react-router-dom'
import {getOrganisationListApi} from 'utils/api/auth/organisationService'
import {KTFilterBox} from 'helpers/ui-components/KTFilterBox'
import {fetchFilterConfig} from 'utils/api/auth/commonService'
import {createFilterPayload} from 'utils/helpers/genericFilterForm'

type OrganisationDTO = {
 id: number
 name: string
 type: string
 state: string
 district: string
 status: string
 user_associated: string
 created_at: string
 created_by: string
}

const ViewOrganisation = () => {
 const {t} = useTranslation()
 const navigate = useNavigate()

 const [filterFormData, setFilterFormData] = useState({})
 const [loader, setLoader] = useState(false)

 const [filterConfig, setFilterConfig] = useState<
  IFormField[]
 >([])

 const [filterCount, setFilterCount] = useState(0)

 const getFilterConfigs = async () => {
  setLoader(true)

  const res = await fetchFilterConfig({
   filter_type: 'USER',
   filter_key: 'ORGANISATION_FILTERS',
  })

  if (res) {
   setFilterConfig(res?.applicableFilters ?? [])
  }

  setLoader(false)
 }

 const cleanPayload = (obj: IFormData) => {
  return Object.fromEntries(
   Object.entries(obj).filter(
    ([, value]) =>
     value !== undefined && value !== null && value !== ''
   )
  )
 }

 useEffect(() => {
  void getFilterConfigs()
 }, [])

 const organisationFetcher = async (
  page: number,
  pageSize: number,
  query?: IFormField
 ) => {
  try {
   let finalParams: IFormData = {
    page,
    page_size: pageSize,
    ...(query || {}),
   }
   if (query?.search) {
    finalParams.name = query.search
   }
   finalParams = cleanPayload(finalParams)
   const res = await getOrganisationListApi(finalParams)

   return {
    data: res?.results || [],
    total: res?.count || 0,
   }
  } catch {
   return {
    data: [],
    total: 0,
   }
  }
 }

 const {
  data: tableData,
  total: totalRecord,
  currentPage,
  itemsPerPage,
  setCurrentPage,
  setItemsPerPage,
  setSearchTerm,
  applyFilters,
 } = useListController<OrganisationDTO>({
  fetcher: organisationFetcher,
  initialPageSize: 10,
  setLoader,
 })

 const columns: Array<ColumnProps<OrganisationDTO>> = [
  {
   key: 'sr',
   title: t('organisation.organisationList.srNo'),
   className: 'text-center w-[80px]',
   render: (_, __, index?: number) => {
    if (index === undefined) return '-'
    return (currentPage - 1) * itemsPerPage + index + 1
   },
  },
  {
   key: 'name',
   title: t('organisation.organisationList.name'),
   render: (_, record) => <p>{record.name}</p>,
  },
  {
   key: 'type',
   title: t('organisation.organisationList.type'),
   render: (_, record) => <p>{record.type}</p>,
  },
  {
   key: 'state',
   title: t('organisation.organisationList.state'),
   render: (_, record) => <p>{record.state}</p>,
  },
  {
   key: 'district',
   title: t('organisation.organisationList.district'),
   render: (_, record) => <p>{record.district || '-'}</p>,
  },
  {
   key: 'status',
   title: t('organisation.organisationList.status'),
   render: (_, record) => (
    <p
     className={`${
      record.user_associated === 'Vacant'
       ? 'text-red-500'
       : 'text-green-600'
     }`}
    >
     {record.user_associated}
    </p>
   ),
  },
  {
   key: 'org_id',
   title: t('organisation.organisationList.orgId'),
   render: (_, record) => (
    <div>
     <p>{record.created_at}</p>
    </div>
   ),
  },
  {
   key: 'action',
   title: t('organisation.organisationList.action'),
   className: 'text-center',
   render: (_, record) => (
    <div
     onClick={() =>
      navigate(`view-organisation-form/${record.id}`)
     }
     className='text-[#4E83E4] cursor-pointer underline'
    >
     {t('organisation.organisationList.viewDetails')}
    </div>
   ),
  },
 ]

 return (
  <>
   {loader && (
    <div className='fixed inset-0 z-[9999] flex items-center justify-center bg-black/30'>
     <Spinner />
    </div>
   )}

   <p className='text-2xl font-bold'>
    {t('organisation.organisationList.allOrg')}
   </p>

   <KTFilterBox
    parentId={'organisation'}
    btnText={t('helperText.filter')}
    filterConfig={filterConfig}
    setLoader={setLoader}
    filterCount={filterCount}
    filterFormData={filterFormData}
    setFilterFormData={async (data) => {
     setFilterFormData(data)
     const payload = await createFilterPayload(
      filterConfig,
      data
     )

     setFilterCount(payload.totalAppliedFilters)
     applyFilters(data)
     setCurrentPage(1)
    }}
   >
    <div
     className='flex-1 bg-white'
     style={{
      marginRight: 16,
      marginLeft: 16,
     }}
    >
     <KTSearchBox
      label={t('organisation.search')}
      placeholder={t('organisation.organisationByName')}
      setSearchTerm={setSearchTerm}
     />
    </div>
    <div
     onClick={() => navigate('view-organisation-form')}
     className='whitespace-nowrap inline-flex items-center bg-[#5B7EE5] text-white font-semibold text-sm px-4 py-2 rounded-lg cursor-pointer'
    >
     <span className='text-lg mr-1'>+</span>
     {t('organisation.addOrganisation')}
    </div>
   </KTFilterBox>

   <div className='mt-6'>
    <KTTable
     data={tableData}
     columns={columns}
     headerClass='bg-[#80868C]'
     rowClass='even:bg-[#F5F8FA]'
     colClass='!text-sm'
     showPagination={false}
    />
   </div>

   <div className='mt-4'>
    <KTPagination
     currentPage={currentPage}
     itemsPerPage={itemsPerPage}
     totalRecord={totalRecord}
     onPageChange={setCurrentPage}
     setItemsPerPage={setItemsPerPage}
    />
   </div>
  </>
 )
}

export default ViewOrganisation
